import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Sparkles, Shield, Wrench, Megaphone, Star, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  feature:      { label: 'New',      color: 'bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30',      Icon: Sparkles },
  improvement:  { label: 'Improved', color: 'bg-[#AF52DE]/10 text-[#AF52DE] border-[#AF52DE]/30',      Icon: Star },
  fix:          { label: 'Fix',      color: 'bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30',      Icon: Wrench },
  security:     { label: 'Security', color: 'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30',      Icon: Shield },
  announcement: { label: 'News',     color: 'bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30',      Icon: Megaphone },
};

const WhatsNewPopover = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [lastViewed, setLastViewed] = useState('');
  const [loaded, setLoaded] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/changelog/unread-count`, { headers });
      setUnread(r.data.unread || 0);
      setLastViewed(r.data.last_viewed_at || '');
    } catch { /* silent */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUnread(); }, [fetchUnread]);
  useEffect(() => {
    // Refresh unread count every 5 minutes so admins' newly-published entries trickle in
    if (!token) return;
    const id = setInterval(fetchUnread, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [token, fetchUnread]);

  const handleOpen = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && !loaded) {
      try {
        const r = await axios.get(`${API}/changelog`, { headers, params: { limit: 12 } });
        setEntries(r.data.entries || []);
        setLoaded(true);
      } catch { /* silent */ }
    }
    if (isOpen && unread > 0) {
      // Optimistically zero the badge; API call is fire-and-forget
      setUnread(0);
      try {
        const r = await axios.post(`${API}/changelog/mark-read`, {}, { headers });
        setLastViewed(r.data.last_viewed_at);
      } catch { /* silent */ }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className="relative"
          aria-label="What's new"
          data-testid="whats-new-trigger"
        >
          <Sparkles className="w-5 h-5 text-[#002FA7]" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-[#FF3B30] text-white text-[9px] font-semibold flex items-center justify-center px-1"
              data-testid="whats-new-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="whats-new-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#002FA7]" />
            <p className="text-sm font-semibold text-[#0A0A0A]">What's New</p>
          </div>
          <button
            onClick={() => { setOpen(false); navigate('/whats-new'); }}
            className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-0.5"
            data-testid="whats-new-see-all"
          >
            See all <ExternalLink className="w-3 h-3" />
          </button>
        </div>
        <ScrollArea className="max-h-[480px]">
          {entries.length === 0 && (
            <div className="p-6 text-center">
              <Sparkles className="w-8 h-8 mx-auto text-[#E5E7EB] mb-2" />
              <p className="text-xs text-[#9CA3AF]">Nothing to show yet.</p>
            </div>
          )}
          {entries.map((e, idx) => {
            const meta = CATEGORY_META[e.category] || CATEGORY_META.feature;
            const isNew = lastViewed ? e.published_at > lastViewed : idx < 3;
            return (
              <button
                key={e.entry_id}
                onClick={() => { setOpen(false); navigate(`/whats-new#${e.entry_id}`); }}
                className="w-full text-left px-3 py-2.5 border-b border-[#F3F4F6] hover:bg-[#FAFAFA] last:border-0"
                data-testid={`whats-new-entry-${e.entry_id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">
                    <meta.Icon className="w-3.5 h-3.5 text-[#4B5563]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={`text-[9px] px-1.5 py-0 h-[16px] border ${meta.color}`}>{meta.label}</Badge>
                      {e.is_pinned && <Badge variant="outline" className="text-[9px] px-1 py-0 h-[16px] text-[#FF9500] border-[#FF9500]/40">Pinned</Badge>}
                      {isNew && <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30]" />}
                      <span className="text-[10px] text-[#9CA3AF] ml-auto">
                        {e.published_at ? formatDistanceToNow(new Date(e.published_at), { addSuffix: true }) : ''}
                      </span>
                    </div>
                    <p className="text-[13px] font-semibold text-[#0A0A0A] mt-1 leading-tight">{e.title}</p>
                    <p className="text-[11px] text-[#4B5563] mt-0.5 line-clamp-2">{e.summary}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default WhatsNewPopover;
